import {FC, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {DateTime} from "luxon";
import {getUserAchievementsByName, getBadgeModalName} from "modules/selectors";
import {hideBadgeModal} from "modules/actions";
import {useIsMobile} from "modules/utils";
import {Badge, PrimaryButton} from "components";
import {ButtonsBox, ModalText, ModalTitle} from "./common";
import {BaseModal} from "./BaseModal";

const ImageContainer = styled.div`
	display: flex;
	justify-content: center;
	margin-bottom: 16px;
`;

const EarnedText = styled.div`
	font-weight: 500;
	margin-bottom: 20px;
`;

const Button = styled(PrimaryButton)`
	&,
	&:hover:not(:disabled) {
		color: ${({theme}) => theme.color.tertiary};
	}
`;

export const BadgeModal: FC = () => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const {isMobile} = useIsMobile();
	const badgeModalName = useSelector(getBadgeModalName);
	const achievement = useSelector(getUserAchievementsByName)(badgeModalName);
	const isOpen = Boolean(badgeModalName);
	const size = isMobile ? "120px" : "170px";

	const handleClose = useCallback(() => {
		dispatch(hideBadgeModal());
	}, [dispatch]);

	if (!isOpen || !achievement) {
		return null;
	}

	return (
		<BaseModal handleClose={handleClose}>
			<ImageContainer>
				<Badge achievement={achievement} height={size} width={size} className="dark" />
			</ImageContainer>

			{achievement.earnedAt && (
				<EarnedText>
					<span>{t("badges.modal.earned_at")} </span>
					<span>{DateTime.fromISO(achievement.earnedAt).toFormat("MM/dd/yy")}</span>
				</EarnedText>
			)}
			<ModalTitle>{t(`badges.name.${achievement.name}`)}</ModalTitle>
			<ModalText>{t(`badges.desc.${achievement.name}`)}</ModalText>
			<ButtonsBox>
				<Button onClick={handleClose}>{t("badges.modal.button")}</Button>
			</ButtonsBox>
		</BaseModal>
	);
};
